import React, { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "./supabase/auth";
import { useTranslation } from 'react-i18next';
import { fuzzyScoreFields } from "./helpers/fuzzy";
import {
  Ingredient,
  saveIngredient,
  PROTEIN_CLASSES,
  PROTEIN_CLASS_LABELS,
  ProteinClass,
  toProteinClass,
} from "./helpers/saveIngredient";

type SortDir = "asc" | "desc";
type ColKey = keyof Ingredient;

const mono = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

const frozenShadow = "2px 0 4px rgba(0,0,0,0.1)";

const baseTh: React.CSSProperties = {
  padding: "4px 8px",
  textAlign: "left",
  fontSize: "11px",
  fontWeight: 700,
  color: "#000000",
  textTransform: "uppercase",
  borderBottom: "2px solid #999999",
  border: "1px solid #d0d0d0",
  background: "#f0f0f0",
  whiteSpace: "nowrap",
  lineHeight: "28px",
  cursor: "pointer",
  userSelect: "none",
  position: "relative",
};

const td: React.CSSProperties = {
  padding: "4px 8px",
  fontSize: "12px",
  fontWeight: 400,
  color: "#000000",
  border: "1px solid #d0d0d0",
  background: "#ffffff",
  whiteSpace: "nowrap",
  lineHeight: "22px",
  // tableLayout is fixed, so without this long values paint across the next
  // column. Clip with an ellipsis; the column-resize drag still reveals all.
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const tdMono: React.CSSProperties = { ...td, fontFamily: mono };
const tdNum: React.CSSProperties = { ...td, textAlign: "right", fontFamily: mono };
const tdNull: React.CSSProperties = { ...tdNum, color: "#999999" };
const tdNullLeft: React.CSSProperties = { ...td, color: "#999999" };

/* columns defined inside component to use t() */

function sortIndicator(col: ColKey, sortCol: ColKey, sortDir: SortDir) {
  if (col !== sortCol) return " \u25BD";
  return sortDir === "asc" ? " \u25B2" : " \u25BC";
}

function compareValues(a: unknown, b: unknown, dir: SortDir): number {
  const nullA = a === null || a === undefined;
  const nullB = b === null || b === undefined;
  if (nullA && nullB) return 0;
  if (nullA) return 1;
  if (nullB) return -1;
  const mul = dir === "asc" ? 1 : -1;
  if (typeof a === "number" && typeof b === "number") return (a - b) * mul;
  if (Array.isArray(a) && Array.isArray(b)) return String(a.join(",")).localeCompare(String(b.join(","))) * mul;
  return String(a).localeCompare(String(b)) * mul;
}

const btnStyle: React.CSSProperties = {
  padding: "4px 14px",
  fontSize: "12px",
  fontWeight: 600,
  border: "1px solid #d0d0d0",
  background: "#f0f0f0",
  cursor: "pointer",
  color: "#000000",
};

export default function IngredientsListScreen({
  onSelectIngredient,
  onAddIngredient,
}: {
  onSelectIngredient?: (id: string) => void;
  onAddIngredient?: () => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === "ko" ? "ko" : "en";
  const classLabel = (c: ProteinClass) =>
    t(PROTEIN_CLASS_LABELS[c].key, { defaultValue: PROTEIN_CLASS_LABELS[c][lang] });

  const columns: { key: ColKey; label: string; align: "left" | "right" }[] = [
    { key: "id", label: t('ingredientList.id'), align: "left" },
    { key: "common_name", label: t('ingredientList.commonName'), align: "left" },
    { key: "korean_name", label: t('ingredientList.koreanName'), align: "left" },
    { key: "fda_name", label: t('ingredientList.fdaName'), align: "left" },
    { key: "supplier", label: t('ingredientList.supplier'), align: "left" },
    { key: "tags", label: t('ingredientList.tags'), align: "left" },
    { key: "protein_per_100g", label: t('ingredientList.proteinG100g'), align: "right" },
    { key: "protein_class", label: t('ingredientList.class', { defaultValue: lang === "ko" ? "분류" : "Class" }), align: "left" },
    { key: "digestibility", label: t('ingredientList.proteinDigestibility'), align: "right" },
    { key: "fat", label: t('ingredientList.fat'), align: "right" },
    { key: "carbs", label: t('ingredientList.carbs'), align: "right" },
    { key: "fiber", label: t('ingredientList.fiber'), align: "right" },
    { key: "sodium", label: t('ingredientList.sodium'), align: "right" },
    { key: "cost_per_kg", label: t('ingredientList.costPerKg'), align: "right" },
  ];

  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [sortCol, setSortCol] = useState<ColKey>("common_name");
  const [colWidths, setColWidths] = useState<Record<string, number>>(() => ({
    id: 110, common_name: 200, korean_name: 140, fda_name: 200, supplier: 140, tags: 220,
    protein_per_100g: 100, protein_class: 150, digestibility: 120, fat: 80, carbs: 80, fiber: 80, sodium: 100, cost_per_kg: 110,
  }));
  const resizeRef = useRef<{ key: string; startX: number; startW: number } | null>(null);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!resizeRef.current) return;
      const { key, startX, startW } = resizeRef.current;
      const newW = Math.max(40, startW + e.clientX - startX);
      setColWidths((prev) => ({ ...prev, [key]: newW }));
    };
    const onUp = () => { resizeRef.current = null; };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
  }, []);

  const startResize = (key: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const th = (e.currentTarget as HTMLElement).parentElement as HTMLElement | null;
    const currentW = colWidths[key] ?? (th ? th.getBoundingClientRect().width : 100);
    resizeRef.current = { key, startX: e.clientX, startW: currentW };
  };
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [tagDropdownOpen, setTagDropdownOpen] = useState(false);

  // Backlog mode: unclassified rows only, above a protein threshold.
  const [todoOnly, setTodoOnly] = useState(false);
  const [minProtein, setMinProtein] = useState("40");
  // Per-row save feedback. Failures stay on screen; they do not revert quietly.
  const [rowState, setRowState] = useState<Record<string, { kind: "saving" | "saved" | "failed"; msg?: string }>>({});
  // What she picked, held separately until the write comes back. A failed pick
  // stays visible in the dropdown next to the error instead of disappearing.
  const [pending, setPending] = useState<Record<string, ProteinClass | null>>({});

  useEffect(() => {
    const fetchIngredients = async () => {
      const { data, error } = await supabase
        .from("ingredients")
        .select("*")
        .eq("is_active", true)
        .order("common_name");
      // A failed query must not render as the "no ingredients yet" empty state.
      if (error) setLoadError(error.message);
      else setIngredients((data as Ingredient[]) || []);
      setLoading(false);
    };
    fetchIngredients();
  }, []);

  // Collect all unique tags
  const allTags = useMemo(() => {
    const tagSet = new Set<string>();
    for (const ing of ingredients) {
      if (ing.tags) for (const t of ing.tags) tagSet.add(t);
    }
    return Array.from(tagSet).sort();
  }, [ingredients]);

  const filtered = useMemo(() => {
    let result = ingredients;

    if (search.trim()) {
      const q = search.toLowerCase();
      const substringMatch = (ing: (typeof ingredients)[number]) => {
        if (ing.common_name?.toLowerCase().includes(q)) return true;
        if (ing.korean_name?.toLowerCase().includes(q)) return true;
        if (String(ing.id).toLowerCase().includes(q)) return true;
        if (ing.fda_name?.toLowerCase().includes(q)) return true;
        if (ing.supplier?.toLowerCase().includes(q)) return true;
        if (ing.tags?.some((t) => t.toLowerCase().includes(q))) return true;
        if (ing.updated_by_email?.toLowerCase().includes(q)) return true;
        if (ing.created_at && new Date(ing.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }).toLowerCase().includes(q)) return true;
        if (ing.updated_at && new Date(ing.updated_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }).toLowerCase().includes(q)) return true;
        return false;
      };
      const direct = result.filter(substringMatch);
      // Typo-tolerant fallback: rank fuzzy name matches after exact ones.
      const fuzzy = result
        .filter((ing) => !substringMatch(ing))
        .map((ing) => ({
          ing,
          score: fuzzyScoreFields(search, [
            ing.common_name,
            ing.korean_name,
            ing.fda_name,
          ]),
        }))
        .filter((x) => x.score > 0)
        .sort((a, b) => a.score - b.score)
        .map((x) => x.ing);
      result = [...direct, ...fuzzy];
    }

    if (selectedTags.length > 0) {
      result = result.filter((ing) =>
        selectedTags.every((t) => ing.tags && ing.tags.includes(t)),
      );
    }

    if (todoOnly) {
      const floor = minProtein.trim() === "" ? null : Number(minProtein);
      result = result.filter((ing) => {
        if (ing.protein_class != null) return false;
        if (floor == null || Number.isNaN(floor)) return true;
        return (ing.protein_per_100g ?? 0) >= floor;
      });
    }

    return result;
  }, [ingredients, search, selectedTags, todoOnly, minProtein]);

  const unclassifiedCount = useMemo(
    () => ingredients.filter((ing) => ing.protein_class == null).length,
    [ingredients],
  );

  const sorted = useMemo(
    () => [...filtered].sort((a, b) => {
      if (sortCol === "korean_name") {
        return compareValues(a.korean_name ?? "", b.korean_name ?? "", sortDir);
      }
      return compareValues((a as any)[sortCol], (b as any)[sortCol], sortDir);
    }),
    [filtered, sortCol, sortDir],
  );

  const handleSort = (col: ColKey) => {
    if (col === sortCol) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortCol(col);
      setSortDir("asc");
    }
  };

  // Inline class edit. Goes through saveIngredient("edit") — the same path the
  // ingredient edit screen uses — so the change log and the concurrency check
  // both still apply. Unset is written as null, never as an empty string.
  const setClass = async (row: Ingredient, raw: string) => {
    const next: ProteinClass | null = toProteinClass(raw);
    if (next === row.protein_class) {
      // Back to what is already stored: drop any failed attempt and its message.
      setPending((p) => { const nextP = { ...p }; delete nextP[row.id]; return nextP; });
      setRowState((s) => { const nextS = { ...s }; delete nextS[row.id]; return nextS; });
      return;
    }

    setPending((p) => ({ ...p, [row.id]: next }));
    setRowState((s) => ({ ...s, [row.id]: { kind: "saving" } }));

    const { data: userData } = await supabase.auth.getUser();
    const email = userData?.user?.email || "unknown";
    const result = await saveIngredient({ ...row, protein_class: next }, "edit", row, email);

    if (!result.success || !result.data) {
      // Keep the pick on screen with the reason. Nothing snaps back in silence.
      setRowState((s) => ({
        ...s,
        [row.id]: { kind: "failed", msg: result.errors[0] || t('ingredientList.saveFailed', { defaultValue: lang === "ko" ? "저장 실패" : "Save failed" }) },
      }));
      return;
    }

    const saved = result.data;
    setIngredients((prev) => prev.map((r) => (r.id === saved.id ? saved : r)));
    setPending((p) => { const nextP = { ...p }; delete nextP[saved.id]; return nextP; });
    setRowState((s) => ({ ...s, [saved.id]: { kind: "saved" } }));
  };

  const toggleTag = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
    );
  };

  return (
    <div style={{ background: "transparent", padding: "8px", minHeight: "100%" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px", flexWrap: "wrap" }}>
        {onAddIngredient && (
          <button
            style={{
              padding: "4px 10px",
              fontSize: "12px",
              fontWeight: 500,
              border: "1px solid #c8c8c8",
              background: "#ffffff",
              cursor: "pointer",
              color: "#000000",
              lineHeight: "18px",
            }}
            onClick={onAddIngredient}
          >
            {t('ingredientList.addButton')}
          </button>
        )}

        <input
          type="text"
          placeholder={t('ingredientList.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            background: "#ffffff",
            border: "1px solid #c8c8c8",
            borderRadius: "0",
            padding: "4px 8px",
            fontSize: "12px",
            color: "#000000",
            outline: "none",
            width: "280px",
          }}
        />

        {/* Tag filter */}
        {allTags.length > 0 && (
          <div style={{ position: "relative" }}>
            <button
              style={{ ...btnStyle, fontSize: "11px" }}
              onClick={() => setTagDropdownOpen((o) => !o)}
            >
              {t('ingredientList.tags')}{selectedTags.length > 0 ? ` (${selectedTags.length})` : ""} ▾
            </button>
            {tagDropdownOpen && (
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  zIndex: 20,
                  background: "#ffffff",
                  border: "1px solid #d0d0d0",
                  maxHeight: "200px",
                  overflowY: "auto",
                  minWidth: "160px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                }}
              >
                {allTags.map((tag) => (
                  <label
                    key={tag}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: "4px 10px",
                      fontSize: "12px",
                      cursor: "pointer",
                      color: "#000",
                      borderBottom: "1px solid #f0f0f0",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedTags.includes(tag)}
                      onChange={() => toggleTag(tag)}
                    />
                    {tag}
                  </label>
                ))}
                {selectedTags.length > 0 && (
                  <button
                    style={{ ...btnStyle, width: "100%", fontSize: "11px", borderRadius: 0, borderTop: "1px solid #d0d0d0" }}
                    onClick={() => { setSelectedTags([]); setTagDropdownOpen(false); }}
                  >
                    {t('ingredientList.clear')}
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Backlog filter: unclassified rows worth classifying */}
        <label style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "12px", color: "#333333", whiteSpace: "nowrap", cursor: "pointer" }}>
          <input type="checkbox" checked={todoOnly} onChange={(e) => setTodoOnly(e.target.checked)} />
          {t('ingredientList.toClassify', { defaultValue: lang === "ko" ? "미분류만" : "To classify" })}
        </label>

        {todoOnly && (
          <label style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "12px", color: "#333333", whiteSpace: "nowrap" }}>
            {t('ingredientList.proteinAtLeast', { defaultValue: lang === "ko" ? "단백질 ≥" : "Protein ≥" })}
            <input
              type="number"
              step="1"
              min="0"
              value={minProtein}
              onChange={(e) => setMinProtein(e.target.value)}
              style={{ width: "56px", background: "#ffffff", border: "1px solid #c8c8c8", padding: "3px 6px", fontSize: "12px", color: "#000000", outline: "none" }}
            />
          </label>
        )}

        <span style={{ color: "#666666", fontSize: "12px", whiteSpace: "nowrap" }}>
          {search.trim() || selectedTags.length > 0 || todoOnly
            ? `${sorted.length} ${t('ingredientList.of')} ${ingredients.length} ${t('ingredientList.ingredients')}`
            : `${ingredients.length} ${t('ingredientList.ingredients')}`}
        </span>

        <span style={{ fontSize: "11px", color: unclassifiedCount > 0 ? "#996600" : "#666666", background: unclassifiedCount > 0 ? "#fff9db" : "transparent", border: "1px solid #e0d8b0", padding: "1px 6px", whiteSpace: "nowrap" }}>
          {unclassifiedCount} {t('ingredientList.unclassified', { defaultValue: lang === "ko" ? "미분류" : "unclassified" })}
        </span>

      </div>

      {loadError && (
        <div style={{ border: "1px solid #cc0000", background: "#fff0f0", padding: "8px", marginBottom: "8px", fontSize: "12px", color: "#cc0000" }}>
          {t('compare.loadFailed', { defaultValue: 'Load failed' })}: {loadError}
        </div>
      )}
      {loading ? (
        <div style={{ color: "#666666", padding: "20px", textAlign: "center", fontSize: "12px" }}>
          {t('ingredientList.loadingIngredients')}
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ tableLayout: "fixed", width: columns.reduce((s, c) => s + (colWidths[c.key as string] ?? 120), 0) + "px", borderCollapse: "collapse", border: "1px solid #d0d0d0", background: "#ffffff" }}>
            <thead>
              <tr>
                {columns.map((col, ci) => {
                  const frozen = ci === 0;
                  const colW = colWidths[col.key];
                  const style: React.CSSProperties = {
                    ...baseTh,
                    textAlign: col.align,
                    ...(colW ? { width: `${colW}px`, minWidth: `${colW}px`, maxWidth: `${colW}px` } : {}),
                    ...(frozen && {
                      position: "sticky",
                      left: 0,
                      zIndex: 2,
                      boxShadow: frozenShadow,
                      background: "#f0f0f0",
                    }),
                  };
                  return (
                    <th
                      key={col.key}
                      style={style}
                      onClick={() => handleSort(col.key)}
                      onMouseEnter={(e) => { e.currentTarget.style.background = "#e8e8e8"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = "#f0f0f0"; }}
                    >
                      {col.label}
                      <span style={{ color: col.key === sortCol ? "#000000" : "#999999", fontSize: "10px" }}>
                        {sortIndicator(col.key, sortCol, sortDir)}
                      </span>
                      <span
                        onMouseDown={(e) => startResize(col.key, e)}
                        onClick={(e) => e.stopPropagation()}
                        title="Drag to resize column"
                        style={{ position: "absolute", top: 0, right: 0, bottom: 0, width: "6px", cursor: "col-resize", background: "rgba(0,0,0,0.18)" }}
                      />
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {sorted.map((ing) => (
                <tr
                  key={ing.id}
                  style={{ cursor: onSelectIngredient ? "pointer" : undefined }}
                  onClick={() => onSelectIngredient?.(ing.id)}
                  onMouseEnter={(e) => {
                    for (const cell of Array.from(e.currentTarget.cells)) {
                      cell.style.background = "#f5f5f5";
                    }
                  }}
                  onMouseLeave={(e) => {
                    for (const cell of Array.from(e.currentTarget.cells)) {
                      cell.style.background = "#ffffff";
                    }
                  }}
                >
                  <td title={ing.id} style={{ ...tdMono, position: "sticky", left: 0, zIndex: 1, background: "#ffffff", boxShadow: frozenShadow }}>{ing.id}</td>
                  <td title={ing.common_name} style={td}>{ing.common_name}</td>
                  <td title={ing.korean_name || undefined} style={ing.korean_name ? td : tdNullLeft}>{ing.korean_name || "-"}</td>
                  <td title={ing.fda_name || undefined} style={ing.fda_name ? td : tdNullLeft}>{ing.fda_name || "-"}</td>
                  <td title={ing.supplier || undefined} style={ing.supplier ? td : tdNullLeft}>{ing.supplier || "-"}</td>
                  <td style={td}>
                    {ing.tags && ing.tags.length > 0
                      ? ing.tags.map((t, i) => (
                          <span key={i} style={{ display: "inline-block", padding: "0px 4px", background: "#e8e8e8", border: "1px solid #ccc", marginRight: "3px", fontSize: "10px", lineHeight: "18px", color: "#333" }}>{t}</span>
                        ))
                      : <span style={{ color: "#999" }}>-</span>
                    }
                  </td>
                  <td style={ing.protein_per_100g !== null
                    ? { ...tdNum, color: ing.protein_per_100g > 50 ? "#16a34a" : "#000000" }
                    : tdNull}>
                    {ing.protein_per_100g !== null ? ing.protein_per_100g.toFixed(1) : "-"}
                  </td>
                  <td style={td} onClick={(e) => e.stopPropagation()}>
                    <select
                      value={(ing.id in pending ? pending[ing.id] : ing.protein_class) ?? ""}
                      onChange={(e) => setClass(ing, e.target.value)}
                      onMouseDown={(e) => e.stopPropagation()}
                      style={{
                        width: "100%",
                        border: "1px solid #d0d0d0",
                        background: rowState[ing.id]?.kind === "failed" ? "#fff0f0" : "#ffffff",
                        color: (ing.id in pending ? pending[ing.id] : ing.protein_class) ? "#000000" : "#999999",
                        fontSize: "11px",
                        padding: "1px 2px",
                        outline: "none",
                      }}
                    >
                      <option value="">-</option>
                      {PROTEIN_CLASSES.map((c) => (
                        <option key={c} value={c}>{classLabel(c)}</option>
                      ))}
                    </select>
                    {rowState[ing.id] && (
                      <div style={{
                        fontSize: "10px",
                        lineHeight: "14px",
                        color: rowState[ing.id].kind === "failed" ? "#cc0000" : "#666666",
                        whiteSpace: "normal",
                      }}>
                        {rowState[ing.id].kind === "saving" && t('ingredientList.savingShort', { defaultValue: lang === "ko" ? "저장 중" : "Saving" })}
                        {rowState[ing.id].kind === "saved" && t('ingredientList.savedShort', { defaultValue: lang === "ko" ? "저장됨" : "Saved" })}
                        {rowState[ing.id].kind === "failed" && rowState[ing.id].msg}
                      </div>
                    )}
                  </td>
                  <td style={ing.digestibility !== null && (ing.protein_per_100g || 0) > 0 ? tdNum : tdNull}>
                    {ing.digestibility !== null && (ing.protein_per_100g || 0) > 0 ? (ing.digestibility * 100).toFixed(0) : "-"}
                  </td>
                  <td style={ing.fat !== null ? tdNum : tdNull}>
                    {ing.fat !== null ? ing.fat.toFixed(1) : "-"}
                  </td>
                  <td style={ing.carbs !== null ? tdNum : tdNull}>
                    {ing.carbs !== null ? ing.carbs.toFixed(1) : "-"}
                  </td>
                  <td style={ing.fiber !== null ? tdNum : tdNull}>
                    {ing.fiber !== null ? ing.fiber.toFixed(1) : "-"}
                  </td>
                  <td style={ing.sodium !== null ? tdNum : tdNull}>
                    {ing.sodium !== null ? ing.sodium.toFixed(1) : "-"}
                  </td>
                  <td style={ing.cost_per_kg !== null ? tdNum : tdNull}>
                    {ing.cost_per_kg !== null ? `\u20AC${ing.cost_per_kg.toFixed(2)}` : "-"}
                  </td>
                </tr>
              ))}
              {sorted.length === 0 && (
                <tr>
                  <td colSpan={columns.length} style={{ ...td, padding: "20px", textAlign: "center", color: "#999999" }}>
                    {search.trim() || todoOnly || selectedTags.length > 0 ? t('ingredientList.noMatch') : t('ingredientList.noIngredients')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
